import { useState, useEffect } from 'react';
import { analytics, logger } from '../services/analytics';

export interface PrivacySettings {
  errorTracking: boolean;
  performanceMonitoring: boolean;
  sessionReplay: boolean;
  analytics: boolean;
}

const defaultSettings: PrivacySettings = {
  errorTracking: false,
  performanceMonitoring: false,
  sessionReplay: false,
  analytics: false
};

export const usePrivacySettings = () => {
  const [settings, setSettings] = useState<PrivacySettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(false);

  // Carregar configurações salvas
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = () => {
    try {
      const savedSettings = localStorage.getItem('privacy_preferences');
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings) as PrivacySettings;
        setSettings(parsed);
      } else {
        // Se não há configurações salvas, usar consentimento atual
        const hasConsent = analytics.checkUserConsent();
        setSettings({
          errorTracking: hasConsent,
          performanceMonitoring: hasConsent,
          sessionReplay: hasConsent,
          analytics: hasConsent
        });
      }
    } catch (error) {
      logger.error('Erro ao carregar configurações de privacidade', error as Error);
      setSettings(defaultSettings);
    }
  };

  const updateSetting = (key: keyof PrivacySettings, value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveSettings = async (newSettings: PrivacySettings) => {
    setIsLoading(true);
    try {
      // Salvar no localStorage
      localStorage.setItem('privacy_preferences', JSON.stringify(newSettings));
      
      // Verificar se pelo menos uma opção está habilitada
      const hasAnyEnabled = Object.values(newSettings).some(Boolean);
      
      // Atualizar consentimento geral
      if (hasAnyEnabled) {
        analytics.setUserConsent(true);
      } else {
        analytics.setUserConsent(false);
      }

      // Adicionar breadcrumb para rastrear mudanças
      analytics.addBreadcrumb('Configurações de privacidade atualizadas', 'privacy', 'info');
      
      setSettings(newSettings);
      
      return true;
    } catch (error) {
      logger.error('Erro ao salvar configurações', error as Error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const hasAnyEnabled = () => {
    return Object.values(settings).some(Boolean);
  };

  const getEnabledCount = () => {
    return Object.values(settings).filter(Boolean).length;
  };

  const getTotalCount = () => {
    return Object.keys(settings).length;
  };

  return {
    settings,
    isLoading,
    updateSetting,
    saveSettings,
    resetSettings,
    hasAnyEnabled: hasAnyEnabled(),
    enabledCount: getEnabledCount(),
    totalCount: getTotalCount(),
    loadSettings
  };
};
